/**
 * イベントタイトルABテスト用のユーティリティ関数
 * GAイベント送信とタイトル取得を統合
 */

import useABTestV2 from '@/utils/abtest/useABTestV2'
import { EVENT_TITLE_AB_TEST_CONFIG } from '@/utils/abtest/abTestConfig'
import { getEventTitleForABTest, isEventInABTest } from '@/utils/abtest/eventTitleMapping'
import GTM from '@/utils/tracking/gtm'
import { EVENT_TITLE_AB_TEST_VIEW, EVENT_TITLE_AB_TEST_TICKET_CLICK } from '@/utils/constants/gaCustomEventSet'

/**
 * イベントタイトルABテスト用のタイトルを取得
 * @param {Object} event - イベントオブジェクト
 * @returns {string} - 表示するタイトル
 */
export const getEventTitleWithABTest = event => {
  if (!event || !event.id) return event?.title || ''

  if (!isEventInABTest(event.id)) {
    return event.title
  }

  const { isAPattern, isBPattern } = useABTestV2(EVENT_TITLE_AB_TEST_CONFIG)

  if (!isAPattern && !isBPattern) {
    return event.title
  }

  const abTestTitle = getEventTitleForABTest(event.id, isBPattern)

  return abTestTitle || event.title
}

/**
 * イベントタイトルABテスト用のGAイベントを送信
 * @param {number} eventId - イベントID
 * @param {boolean} isBPattern - Bパターンかどうか
 * @param {string} context - コンテキスト ('list' | 'detail')
 * @param {string} source - ソース ('from_list' | null)
 */
export const sendEventTitleABTestGA = (eventId, isBPattern, context, source = null) => {
  if (!isEventInABTest(eventId)) return

  const group = isBPattern ? 'b' : 'a'
  let eventLabel = ''

  switch (context) {
    case 'list':
      eventLabel = `list_${group}`
      break
    case 'detail':
      if (source === 'from_list') {
        eventLabel = `detail_${group}_from_list`
      } else {
        eventLabel = `detail_${group}`
      }
      break
    case 'ticket':
      eventLabel = `ticket_${group}`
      break
    default:
      return // 不明なコンテキストの場合は送信しない
  }

  GTM.sendEventToGa({
    ...EVENT_TITLE_AB_TEST_VIEW,
    label: eventLabel
  })
}

/**
 * チケット選択時のGAイベントを送信
 * @param {number} eventId - イベントID
 */
export const sendTicketClickABTestGA = eventId => {
  if (!isEventInABTest(eventId)) return

  const { isBPattern } = useABTestV2(EVENT_TITLE_AB_TEST_CONFIG)
  const group = isBPattern ? 'b' : 'a'

  GTM.sendEventToGa({
    ...EVENT_TITLE_AB_TEST_TICKET_CLICK,
    label: `ticket_${group}`
  })
}

/**
 * イベント一覧でのビューイベント送信
 * @param {Object} event - イベントオブジェクト
 */
export const sendListViewABTestGA = event => {
  if (!event || !isEventInABTest(event.id)) return

  const { isBPattern } = useABTestV2(EVENT_TITLE_AB_TEST_CONFIG)
  sendEventTitleABTestGA(event.id, isBPattern, 'list')
}

/**
 * イベント詳細でのビューイベント送信
 * @param {Object} event - イベントオブジェクト
 * @param {boolean} fromList - 一覧経由かどうか
 */
export const sendDetailViewABTestGA = (event, fromList = false) => {
  if (!event || !isEventInABTest(event.id)) return

  const { isBPattern } = useABTestV2(EVENT_TITLE_AB_TEST_CONFIG)
  const source = fromList ? 'from_list' : null
  sendEventTitleABTestGA(event.id, isBPattern, 'detail', source)
}
